import React, { useCallback, useEffect, useRef, useState } from 'react';

import { createRoot } from 'react-dom/client';

import { LoadingMessage, ResponseMessage } from './components';

import type { MouseEvent, ReactNode } from 'react';
import type { Root } from 'react-dom/client';

interface AIInterviewModalProps {
  jobId?: number;
}

const AIInterviewModal = ({ jobId }: AIInterviewModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const renderRootsRef = useRef<Map<HTMLElement, Root>>(new Map());
  const requestInFlightRef = useRef(false);
  const [response, setResponse] = useState<{ index: string; content: string }[]>([]);

  const renderIntoRoot = useCallback(
    (container: HTMLElement | null | undefined, content: ReactNode) => {
      if (!container) return;

      let root = renderRootsRef.current.get(container);
      if (!root) {
        root = createRoot(container);
        renderRootsRef.current.set(container, root);
      }

      root.render(content);
    },
    []
  );

  const generateChatResponse = useCallback(
    async (
      id: string,
      fieldId: number,
      input: string,
      reactComponentRoot: HTMLElement | null = null
    ) => {
      if (!jobId || requestInFlightRef.current) return;

      requestInFlightRef.current = true;
      setIsLoading(true);
      try {
        const apiResponse = await fetch('/api/v4/ai_interviews', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            job_id: jobId,
            field_id: fieldId,
            input
          })
        }).then(async (res) => res.json());
        if (apiResponse?.result) {
          setResponse((prevResponse) => [
            ...prevResponse,
            { index: id, content: apiResponse.result }
          ]);

          renderIntoRoot(
            reactComponentRoot,
            <ResponseMessage id={id} message={apiResponse.result} />
          );
        } else {
          throw new Error('No response from STG GPT');
        }
      } catch (error) {
        console.error(error);
      } finally {
        requestInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [jobId, renderIntoRoot]
  );

  const updateUI = useCallback(
    (felixContainer: HTMLElement, number: string, reactComponentRoot: HTMLElement | null) => {
      const button = felixContainer.querySelector('button.felix');
      const responseMessageContainer = felixContainer.nextElementSibling as HTMLElement;

      if (responseMessageContainer) {
        responseMessageContainer.style.display = 'unset';
        if (button) {
          button.innerHTML = 'Regenerate feedback';
        }
      }

      const dataInputField = document.getElementById(
        `application_answers_attributes_${number}_data`
      ) as HTMLInputElement;

      const fieldId = Number(button?.id.replace('trigger-react-component-', ''));
      if (!Number.isSafeInteger(fieldId)) return;

      renderIntoRoot(reactComponentRoot, <LoadingMessage />);

      generateChatResponse(
        button?.id ?? '',
        fieldId,
        dataInputField?.value ?? '',
        reactComponentRoot
      );
    },
    [generateChatResponse, renderIntoRoot]
  );

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const targetElement = event.target as HTMLElement;
      const fieldContainer = targetElement.closest('.field-container');
      const reactComponentRoot = fieldContainer?.querySelector(
        '#react-component-root'
      ) as HTMLElement | null;
      const felixContainer = targetElement.closest('.felix-container') as HTMLElement;

      const inputField = felixContainer?.previousElementSibling;
      const id = (inputField as HTMLElement)?.id;
      const match = /answers_attributes_(\d+)_/.exec(id);
      const number = match ? match[1] : null;
      if (number !== null) {
        updateUI(felixContainer, number, reactComponentRoot);
      }
    },
    [updateUI]
  );

  useEffect(() => {
    const buttons = document.getElementsByClassName('felix');

    Array.from(buttons).forEach((button) => {
      button.addEventListener('click', handleButtonClick as unknown as EventListener);
    });

    return () => {
      Array.from(buttons).forEach((button) => {
        button.removeEventListener('click', handleButtonClick as unknown as EventListener);
      });
    };
  }, [generateChatResponse, response, isLoading, handleButtonClick]);

  useEffect(
    () => () => {
      renderRootsRef.current.forEach((root) => root.unmount());
      renderRootsRef.current.clear();
    },
    []
  );

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return null;
};

export default AIInterviewModal;
